import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function AdminStatsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30); // 30 jours par défaut

  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    loadStats();
  }, [period]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('adminAuth');
      const response = await axios.get(
        `${backendUrl}/api/stats/admin/overview?days=${period}`,
        {
          headers: {
            'Authorization': `Basic ${token}`
          }
        }
      );
      
      setStats(response.data);
    } catch (err) {
      console.error('Erreur chargement stats:', err);
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-lg">Chargement des statistiques...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 text-red-800 rounded">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">📊 Statistiques du Site</h2>
        <select
          value={period}
          onChange={(e) => setPeriod(parseInt(e.target.value))}
          className="px-4 py-2 border rounded"
        >
          <option value={7}>7 derniers jours</option>
          <option value={30}>30 derniers jours</option>
          <option value={90}>90 derniers jours</option>
          <option value={365}>1 an</option>
        </select>
      </div>

      {stats && (
        <div className="space-y-6">
          {/* Résumé */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-blue-600 text-sm font-semibold mb-1">Total Visites</div>
              <div className="text-3xl font-bold text-blue-900">{stats.total_visits.toLocaleString()}</div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-green-600 text-sm font-semibold mb-1">Visiteurs Uniques</div>
              <div className="text-3xl font-bold text-green-900">{stats.unique_visitors.toLocaleString()}</div>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="text-purple-600 text-sm font-semibold mb-1">Pages / Visiteur</div>
              <div className="text-3xl font-bold text-purple-900">
                {stats.unique_visitors > 0 ? (stats.total_visits / stats.unique_visitors).toFixed(1) : '0'}
              </div>
            </div>
          </div>

          {/* Pages les plus visitées */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-bold mb-4">📄 Pages les Plus Visitées</h3>
            <div className="space-y-2">
              {stats.top_pages.map((page, idx) => {
                const percentage = stats.total_visits > 0 ? (page.visits / stats.total_visits * 100) : 0;
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-32 text-sm font-mono text-gray-600 truncate">{page.page}</div>
                    <div className="flex-1">
                      <div className="bg-gray-200 rounded-full h-6 overflow-hidden">
                        <div
                          className="bg-blue-500 h-full flex items-center justify-end px-2"
                          style={{ width: `${percentage}%` }}
                        >
                          <span className="text-xs text-white font-semibold">
                            {page.visits}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="w-16 text-right text-sm text-gray-600">
                      {percentage.toFixed(1)}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Appareils */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold mb-4">📱 Types d'Appareils</h3>
              <div className="space-y-3">
                {stats.devices.map((device, idx) => {
                  const icons = {
                    desktop: '🖥️',
                    mobile: '📱',
                    tablet: '📱',
                    unknown: '❓'
                  };
                  const labels = {
                    desktop: 'Ordinateur',
                    mobile: 'Mobile',
                    tablet: 'Tablette',
                    unknown: 'Inconnu'
                  };
                  const percentage = stats.total_visits > 0 ? (device.count / stats.total_visits * 100) : 0;
                  
                  return (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{icons[device.type] || icons.unknown}</span>
                        <span className="font-medium">{labels[device.type] || device.type}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{device.count}</div>
                        <div className="text-xs text-gray-500">{percentage.toFixed(1)}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Graphique visites par jour (simple) */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold mb-4">📈 Visites par Jour</h3>
              <div className="space-y-1">
                {stats.daily_visits.slice(-14).map((day, idx) => {
                  const maxVisits = Math.max(...stats.daily_visits.map(d => d.visits));
                  const barWidth = maxVisits > 0 ? (day.visits / maxVisits * 100) : 0;
                  
                  return (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <div className="w-20 text-gray-600">{day.date.slice(5)}</div>
                      <div className="flex-1">
                        <div className="bg-gray-200 rounded h-4 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-yellow-400 to-yellow-600 h-full"
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                      </div>
                      <div className="w-12 text-right font-semibold">{day.visits}</div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 text-xs text-gray-500 text-center">
                Derniers 14 jours
              </div>
            </div>
          </div>

          {/* Informations supplémentaires */}
          <div className="bg-gray-50 border rounded-lg p-4">
            <p className="text-sm text-gray-600">
              📌 <strong>Note:</strong> Les statistiques sont collectées automatiquement depuis la mise en place du système de tracking. 
              Les visiteurs uniques sont identifiés par session de navigation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
